import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {MatSelect} from '@angular/material/select';
import {KeyValueMapping} from '../../../types/common.types';


/**
 * Return value type for the multi-select component
 */
export enum DataType {
  Default = 'strings',
  HashMap = 'hashMap',
}

@Component({
  selector: 'app-element-multi-select',
  templateUrl: './element-multi-select.component.html',
  styleUrls: ['./element-multi-select.component.scss']
})
export class ElementMultiselectComponent implements OnInit, OnChanges {
  readonly SELECT_ALL = 'Select All';

  selectFormControl = new FormControl();

  @Input() qaAttr: string;
  @Input() displayLabel: string;
  @Input() defaultValues: string[] = [];
  @Input() dataType: DataType = DataType.HashMap;
  @Input() cssClasses: string[] = [];
  @Input() placeholder = 'Select Options';
  @Input() options: string[] = [];
  @Input() selectedValues: string[] | KeyValueMapping<any> = [];
  @Input() disabledOptions: string[] = [];
  @Input() disabledOptionTooltip = '';
  @Input() requiredErrorMessage: string;
  @Input() disabled = false;
  @Output() selectedValuesChange: EventEmitter<string[] | KeyValueMapping<{ [key: string]: any }>> =
    new EventEmitter<string[] | KeyValueMapping<{ [key: string]: any }>>();
  @Output() onOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onClosed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild(MatSelect, {static: true}) select: MatSelect;

  ngOnInit() {
    if (this.requiredErrorMessage && !this.selectFormControl.hasValidator(Validators.required)) {
      this.selectFormControl.addValidators(Validators.required);
      this.selectFormControl.updateValueAndValidity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultValues && changes.defaultValues.currentValue) {
      const defaultValues = changes.defaultValues.currentValue || this.defaultValues;
      this.selectFormControl.patchValue(defaultValues);
    }
    if (changes.selectedValues && changes.selectedValues.currentValue) {
      if (this.dataType === DataType.Default) {
        this.selectFormControl.patchValue(changes.selectedValues.currentValue as string[]);
      }
      else {
        const selectedObject: KeyValueMapping<any> = changes.selectedValues.currentValue;
        const selectedValues: string[] = Object.keys(selectedObject).filter((key: string) => selectedObject[key]);
        this.selectFormControl.patchValue(selectedValues);
      }
    }
  }

  onSelectPanelToggle(isOpened: boolean): void {
    if (!isOpened) {
      this.onClosed.emit(true);
    }
    else {
      this.onOpen.emit(true);
    }
  }

  toggleAll() {
    if (this.selectFormControl.value.includes(this.SELECT_ALL)) {
      this.selectFormControl.patchValue([this.SELECT_ALL, ...this.enabledOptions]);
    }
    else {
      this.selectFormControl.patchValue([]);
    }
    this.onSelectionChange(this.selectFormControl.value);
  }

  togglePerOption() {
    const values: string[] = this.selectFormControl.value;

    if (values.includes(this.SELECT_ALL)) {
      const selectedValues = values.filter((value) => value !== this.SELECT_ALL && !this.isOptionDisabled(value));
      this.selectFormControl.patchValue(selectedValues);
    }
    else if (values.length == this.enabledOptions.length) {
      this.selectFormControl.patchValue([this.SELECT_ALL, ...this.enabledOptions]);
    }
    else {
      this.selectFormControl.patchValue(values.filter((value) => !this.isOptionDisabled(value)));
    }
    this.onSelectionChange(this.selectFormControl.value);
  }

  isOptionDisabled(option: string): boolean {
    return this.disabledOptions.includes(option);
  }

  getOptionTooltip(option: string): string {
    return this.isOptionDisabled(option) ? this.disabledOptionTooltip : '';
  }

  private get enabledOptions(): string[] {
    return this.options.filter((option: string) => !this.isOptionDisabled(option));
  }

  private onSelectionChange(values: string[]): void {
    values = values.filter((value: string) => value === this.SELECT_ALL || !this.isOptionDisabled(value));

    if (this.dataType === DataType.Default) {
      this.selectedValuesChange.emit(values as string[]);
    }
    else {
      const selectedObject: KeyValueMapping<any> = {};
      values.forEach((value: string) => (selectedObject[value] = true));
      this.selectedValuesChange.emit(selectedObject);
    }
  }
}
